import React, { useState, useEffect } from 'react';
import './Search.css';
import NFTCard from './NFTCard';
import rarityScores from './nft-rarity-scores.json';
import nftMetadata from './nft-metadata.json';
import { FaTimes } from 'react-icons/fa';
import CustomDropdown from './CustomDropdown';

function Search() {
  const [nfts, setNfts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    background: '',
    swag: '',
    skin: ''
  });
  const [searchQuery, setSearchQuery] = useState('');

  const extractUniqueTraits = (metadata) => {
    const traits = {
      background: new Set(),
      swag: new Set(),
      skin: new Set()
    };

    Object.values(metadata).forEach(nft => {
      traits.background.add(nft.background);
      traits.swag.add(nft.swag);
      traits.skin.add(nft.skin);
    });

    return {
      background: Array.from(traits.background),
      swag: Array.from(traits.swag),
      skin: Array.from(traits.skin)
    };
  };

  const traits = extractUniqueTraits(nftMetadata);

  const handleModalOpen = () => {
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  useEffect(() => {
    const nftsWithScores = rarityScores.map(score => {
      const metadata = nftMetadata[score.capyId];
      return {
        capyid: score.capyId,
        background: metadata.background,
        water: metadata.water,
        skin: metadata.skin,
        outline: metadata.outline,
        shadows: metadata.shadows,
        mouth: metadata.mouth,
        eyes: metadata.eyes,
        swag: metadata.swag,
        pup: metadata.pup,
        keyimage: metadata.keyimage,
        rarityScore: score.rarityScore,
        rank: score.rank
      };
    });

    setNfts(nftsWithScores);
  }, []);

  useEffect(() => {
    document.body.classList.add('search-background');
    return () => {
      document.body.classList.remove('search-background');
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleClearFilters = () => {
    setFilters({
      background: '',
      swag: '',
      skin: ''
    });
    setSearchQuery('');
  };

  const filteredNfts = nfts.filter(nft => {
    const searchQueryTrimmed = searchQuery.trim().toLowerCase();

    // Check if filters match
    const matchesFilters = Object.keys(filters).every(filterType =>
      !filters[filterType] || nft[filterType].toLowerCase() === filters[filterType].toLowerCase()
    );

    // If the search query contains numbers, look for ID matches
    if (searchQueryTrimmed.includes(',')) {
      // Handle comma-separated IDs
      const searchIds = searchQueryTrimmed.split(',').map(id => id.trim());
      const nftId = nft.capyid.toLowerCase().replace('capy #', '');
      return searchIds.includes(nftId) && matchesFilters;
    } else if (/^\d+$/.test(searchQueryTrimmed)) {
      // Handle single ID search (existing logic)
      const matchesExactId = nft.capyid.toLowerCase() === `capy #${searchQueryTrimmed}`;
      const matchesPartialId = nft.capyid.toLowerCase().includes(searchQueryTrimmed);
      return (matchesExactId || matchesPartialId) && matchesFilters;
    }

    // If not searching for a number, look for trait matches
    const matchesTraits = Object.keys(nft).some(key =>
      key !== 'rarityScore' &&
      key !== 'rank' &&
      key !== 'capyid' &&
      typeof nft[key] === 'string' &&
      nft[key].toLowerCase().includes(searchQueryTrimmed)
    );

    return matchesTraits && matchesFilters;
  });

  return (
    <section className="page-content search-content">
      <div className="search-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by ID, Trait or CSV: '1,2,3,4,5...'"
            className="search-input"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <button className="clear-filters-button" onClick={handleClearFilters}>
            <FaTimes />
          </button>
        </div>

        <div className="filter-container">
          {Object.entries(traits).map(([traitType, options]) => (
            <div key={traitType} className="filter-group">
              <CustomDropdown
                options={options}
                value={filters[traitType]}
                onChange={(value) => handleFilterChange(traitType, value)}
                placeholder={`All ${traitType}s`}
              />
            </div>
          ))}
        </div>

        <div className="search-results">
          <div className="nft-grid">
            {filteredNfts.map((nft) => (
              <NFTCard
                key={nft.capyid}
                nft={nft}
                onModalOpen={handleModalOpen}
                onModalClose={handleModalClose}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Search;